import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { grantToolAccess, revokeToolAccess, type AssignableToolRole, type Member } from "@/lib/api";

type GetToken = () => Promise<string | null>;

interface GrantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: Member | null;
  productId: string;
  orgId: string;
  onSuccess: () => void;
  getToken: GetToken;
}

const ROLE_OPTIONS: AssignableToolRole[] = [
  "tool-admin",
  "tool-manager",
  "tool-member",
  "tool-viewer",
  "denied",
];

const INHERIT_VALUE = "__inherit__";
type RoleSelection = AssignableToolRole | typeof INHERIT_VALUE;

function defaultRoleFor(member: Member | null): RoleSelection {
  if (!member || member.toolRole === "none") return "tool-member";
  return member.toolRole as AssignableToolRole;
}

export function GrantDialog({
  open,
  onOpenChange,
  member,
  productId,
  orgId,
  onSuccess,
  getToken,
}: GrantDialogProps) {
  const [role, setRole] = useState<RoleSelection>(defaultRoleFor(member));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setRole(defaultRoleFor(member));
  }, [open, member?.clerkUserId, member?.toolRole]);

  const hasExplicit = !!member && member.toolRole !== "none";

  async function onSave() {
    if (!member) return;
    setSaving(true);
    try {
      if (role === INHERIT_VALUE) {
        await revokeToolAccess(getToken, { clerkUserId: member.clerkUserId, productId }, orgId);
        toast.success(`Removed override for ${member.displayName ?? member.email ?? "user"} — they will now inherit the org default`);
      } else {
        await grantToolAccess(getToken, { clerkUserId: member.clerkUserId, productId, role }, orgId);
        toast.success(`Granted ${role} to ${member.displayName ?? member.email ?? "user"}`);
      }
      onSuccess();
      onOpenChange(false);
    } catch {
      toast.error("Failed to update access");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{hasExplicit ? "Change Role" : "Grant Tool Access"}</DialogTitle>
          <DialogDescription>
            Set the role for {member?.displayName ?? member?.email ?? "this member"}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <label className="text-sm font-medium" htmlFor="grant-role-select">
            Role
          </label>
          <select
            id="grant-role-select"
            value={role}
            disabled={saving}
            onChange={(e) => setRole(e.target.value as RoleSelection)}
            className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
            {hasExplicit && (
              <option value={INHERIT_VALUE}>inherit (use org default)</option>
            )}
          </select>
        </div>
        <div className="mt-2 flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
